import { Component, signal, PLATFORM_ID, Inject, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClientModule } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BookingService, ReservationRequest } from '../services/booking.service';
import { AuthService } from '../../../core/services/auth.service';
import {DocumentDto} from '@features/home/services/apartment.service';

@Component({
  selector: 'app-booking-details',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, TranslateModule, HttpClientModule],
  template: `
    <div class="booking-details-page">
      <header class="header">
        <div class="header-container">
          <a routerLink="/" class="logo">
            <img src="assets/images/logo%20kia%20ora.png" alt="Serenity Hotel" class="logo-img">
          </a>

          <nav class="nav-links">
            <a routerLink="/">{{ 'NAV.HOME' | translate }}</a>
            <a routerLink="/about">{{ 'NAV.ABOUT' | translate }}</a>
            <a routerLink="/contact">{{ 'NAV.CONTACT' | translate }}</a>
            <a routerLink="/gallery">{{ 'NAV.GALLERY' | translate }}</a>
            <a *ngIf="authService.currentUser$ | async" routerLink="/profile">{{ 'NAV.PROFILE' | translate }}</a>
          </nav>

          <div class="header-actions">
            <ng-container *ngIf="authService.currentUser$ | async as user; else guestLinks">
              <a routerLink="/profile" class="sign-in-link">{{ user.firstName || user.username }}</a>
              <button class="logout-btn" (click)="authService.logout()">{{ 'NAV.LOGOUT' | translate }}</button>
            </ng-container>
            <ng-template #guestLinks>
              <a routerLink="/auth/login" class="sign-in-link">{{ 'NAV.SIGN_IN' | translate }}</a>
            </ng-template>
            <a routerLink="/booking" class="book-now-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {{ 'NAV.BOOK_NOW' | translate }}
            </a>
            <select class="lang-select" (change)="changeLanguage($event)">
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>
      </header>

      @if (errorMessage) {
        <div class="error-banner">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>{{ errorMessage }}</span>
          <button class="close-btn" (click)="errorMessage = ''">&times;</button>
        </div>
      }

      @if (successMessage) {
        <div class="success-banner">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{{ successMessage }}</span>
          <button class="close-btn" (click)="successMessage = ''">&times;</button>
        </div>
      }

      <main class="main-content">
        <div class="booking-container">
          <div class="booking-summary">
            <div class="summary-card">
              <div class="date-section">
                <div class="date-item">
                  <span class="date-label">{{ 'BOOKING_DETAILS.CHECK_IN' | translate }}</span>
                  <span class="date-value">{{ formatDate(booking.checkIn) }}</span>
                  <span class="date-time">{{ 'BOOKING_DETAILS.FROM' | translate }} 12:00:00</span>
                </div>
                <div class="date-item">
                  <span class="date-label">{{ 'BOOKING_DETAILS.CHECK_OUT' | translate }}</span>
                  <span class="date-value">{{ formatDate(booking.checkOut) }}</span>
                  <span class="date-time">{{ 'BOOKING_DETAILS.UNTIL' | translate }} 10:00:01</span>
                </div>
              </div>

              <div class="price-breakdown">
                <div class="price-row">
                  <span>{{ booking.roomName }}</span>
                  <span>{{ booking.roomPrice | number }} FCFA</span>
                </div>
                <div class="price-row">
                  <span>{{ 'BOOKING_DETAILS.ADULTS' | translate }}</span>
                  <span>{{ booking.adults }} {{ 'BOOKING_DETAILS.PERSON' | translate }}</span>
                </div>
                <div class="price-row">
                  <span>{{ 'BOOKING_DETAILS.ROOM_SIZE' | translate }}</span>
                  <span>{{ booking.roomSize }} m2</span>
                </div>
                <div class="price-row">
                  <span>{{ 'BOOKING_DETAILS.NO_OF_ROOM' | translate }}</span>
                  <span>{{ booking.numberOfRooms }}</span>
                </div>
                <div class="price-row">
                  <span>{{ booking.taxRate }}% {{ 'BOOKING_DETAILS.TAX' | translate }}</span>
                  <span>{{ taxAmount | number:'1.0-0' }} FCFA</span>
                </div>
                <div class="price-row">
                  <span>{{ booking.serviceChargeRate }}% {{ 'BOOKING_DETAILS.SERVICE_CHARGE' | translate }}</span>
                  <span>{{ serviceChargeAmount | number:'1.0-0' }} FCFA</span>
                </div>
              </div>

              <div class="promo-section">
                <input type="text" [(ngModel)]="promoCode" [placeholder]="'BOOKING_DETAILS.PROMO_PLACEHOLDER' | translate" class="promo-input" />
                <button class="apply-btn" (click)="applyPromoCode()">{{ 'BOOKING_DETAILS.APPLY_CODE' | translate }}</button>
              </div>

              <div class="total-section">
                <span class="total-label">{{ 'BOOKING_DETAILS.TOTAL_PRICE' | translate }}</span>
                <span class="total-value">{{ totalPrice | number:'1.0-0' }} FCFA <span class="info-icon">ⓘ</span></span>
              </div>
            </div>

            <div class="info-cards">
              <div class="info-card">
                <div class="info-icon clock">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span class="badge">24</span>
                </div>
                <div class="info-content">
                  <h4>{{ 'BOOKING_DETAILS.ALMOST_YOURS' | translate }}</h4>
                  <p>{{ 'BOOKING_DETAILS.ALMOST_YOURS_DESC' | translate }}</p>
                </div>
              </div>

              <div class="info-card">
                <div class="info-icon price">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" stroke-width="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span class="badge red">$</span>
                </div>
                <div class="info-content">
                  <h4>{{ 'BOOKING_DETAILS.NO_SURPRISES' | translate }}</h4>
                  <p>{{ 'BOOKING_DETAILS.NO_SURPRISES_DESC' | translate }}</p>
                </div>
              </div>

              <div class="info-card">
                <div class="info-icon secure">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" stroke-width="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                  <span class="badge green">✓</span>
                </div>
                <div class="info-content">
                  <h4>{{ 'BOOKING_DETAILS.SECURE_BOOKING' | translate }}</h4>
                  <p>{{ 'BOOKING_DETAILS.SECURE_BOOKING_DESC' | translate }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="details-form">
            <h2><span class="red-line"></span> {{ 'BOOKING_DETAILS.ENTER_DETAILS' | translate }}</h2>

            <form (ngSubmit)="onSubmit()">
              <div class="form-row">
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.FIRST_NAME' | translate }} <span class="required">*</span></label>
                  <input type="text" [(ngModel)]="guestDetails.firstName" name="firstName" required />
                </div>
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.LAST_NAME' | translate }} <span class="required">*</span></label>
                  <input type="text" [(ngModel)]="guestDetails.lastName" name="lastName" required />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.EMAIL' | translate }} <span class="required">*</span></label>
                  <input type="email" [(ngModel)]="guestDetails.email" name="email" required />
                </div>
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.PHONE' | translate }} <span class="required">*</span></label>
                  <input type="tel" [(ngModel)]="guestDetails.phone" name="phone" required />
                  <span class="hint">{{ 'BOOKING_DETAILS.PHONE_HINT' | translate }}</span>
                </div>
              </div>

              <div class="form-group full-width">
                <label>{{ 'BOOKING_DETAILS.ADDRESS' | translate }}</label>
                <textarea [(ngModel)]="guestDetails.address" name="address" rows="3"></textarea>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.BIRTH_DATE' | translate }}</label>
                  <input type="date" [(ngModel)]="guestDetails.dateNaissance" name="dateNaissance" />
                </div>
                <div class="form-group">
                  <label>{{ 'BOOKING_DETAILS.CHILDREN' | translate }}</label>
                  <input type="number" [(ngModel)]="occupantDetails.nombreEnfants" name="nombreEnfants" min="0" />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group checkbox-group">
                  <label class="checkbox-label">
                    <input type="checkbox" [(ngModel)]="occupantDetails.avecAnimalCompagnie" name="avecAnimalCompagnie" />
                    {{ 'BOOKING_DETAILS.WITH_PET' | translate }}
                  </label>
                </div>
                <div class="form-group checkbox-group">
                  <label class="checkbox-label">
                    <input type="checkbox" [(ngModel)]="occupantDetails.ageEnfantSuperieurASept" name="ageEnfantSuperieurASept" />
                    {{ 'BOOKING_DETAILS.CHILD_OVER_7' | translate }}
                  </label>
                </div>
              </div>

              <div class="account-notice">
                <div class="account-notice-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <div class="account-notice-text">
                  <strong>Compte requis pour le paiement</strong>
                  <p>Un compte sera créé avec votre adresse email. Après la réservation, connectez-vous pour confirmer et payer en ligne via FedaPay (Wave, Orange, MTN, Moov, Visa).</p>
                </div>
              </div>

              <div class="room-section">
                <h3>{{ 'BOOKING_DETAILS.ROOM' | translate }}</h3>
                <div class="room-card">
                  @if (roomCoverImage) {
                    <img [src]="getImageSrc(roomCoverImage, sanitizer)" [alt]="booking.roomName" class="room-image"/>
                  } @else {
                    <div class="room-image room-image-placeholder"></div>
                  }
                  <div class="room-info">
                    <h4>{{ booking.roomName }}</h4>
                    <p class="room-type">{{ booking.paymentType }}</p>
                    <p class="stay-includes">{{ 'BOOKING_DETAILS.STAY_INCLUDES' | translate }}</p>
                    <a href="#" class="booking-conditions">{{ 'BOOKING_DETAILS.BOOKING_CONDITIONS' | translate }} ↗</a>
                  </div>
                </div>
              </div>

              <div class="form-group full-width">
                <label>{{ 'BOOKING_DETAILS.FULL_GUEST_NAME' | translate }}</label>
                <input type="text" [(ngModel)]="guestDetails.fullName" name="fullName" />
              </div>

              <div class="form-group full-width">
                <label>{{ 'BOOKING_DETAILS.SPECIAL_REQUESTS' | translate }}</label>
                <span class="hint">{{ 'BOOKING_DETAILS.SPECIAL_REQUESTS_HINT' | translate }}</span>
                <textarea [(ngModel)]="guestDetails.specialRequests" name="specialRequests" rows="4"></textarea>
              </div>

              <button type="submit" class="submit-btn" [disabled]="isSubmitting">
                {{ isSubmitting ? 'Enregistrement…' : 'Réserver et procéder au paiement' }}
              </button>
            </form>
          </div>
        </div>
      </main>

      <footer class="footer">
        <div class="footer-container">
          <div class="footer-grid">
            <div class="footer-col">
              <div class="footer-logo">
                <img src="assets/images/logo%20kia%20ora.png" alt="Kia Ora" class="footer-logo-img">
              </div>
              <p class="footer-desc">{{ 'FOOTER.DESCRIPTION' | translate }}</p>
            </div>
            <div class="footer-col">
              <h4>{{ 'BOOKING_DETAILS.FOOTER.QUICK_LINKS' | translate }}</h4>
              <ul>
                <li><a routerLink="/">{{ 'BOOKING_DETAILS.FOOTER.ROOM_BOOKING' | translate }}</a></li>
                <li><a routerLink="/">{{ 'NAV.ROOMS' | translate }}</a></li>
                <li><a routerLink="/">{{ 'NAV.CONTACT' | translate }}</a></li>
                <li><a routerLink="/">{{ 'BOOKING_DETAILS.FOOTER.EXPLORE' | translate }}</a></li>
              </ul>
            </div>
            <div class="footer-col">
              <h4>{{ 'BOOKING_DETAILS.FOOTER.COMPANY' | translate }}</h4>
              <ul>
                <li><a routerLink="/">{{ 'BOOKING_DETAILS.FOOTER.PRIVACY_POLICY' | translate }}</a></li>
                <li><a routerLink="/">{{ 'BOOKING_DETAILS.FOOTER.REFUND_POLICY' | translate }}</a></li>
                <li><a routerLink="/">{{ 'BOOKING_DETAILS.FOOTER.FAQ' | translate }}</a></li>
                <li><a routerLink="/">{{ 'NAV.ABOUT' | translate }}</a></li>
              </ul>
            </div>
            <div class="footer-col">
              <h4>{{ 'BOOKING_DETAILS.FOOTER.SOCIAL_MEDIA' | translate }}</h4>
              <div class="social-links">
                <a href="#" aria-label="Facebook">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a href="#" aria-label="Instagram">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a href="#" aria-label="Twitter">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                  </svg>
                </a>
                <a href="#" aria-label="Pinterest">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div class="footer-bottom">
            <p>Copyright ©2023, All Rights Reserved</p>
          </div>
        </div>
      </footer>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Jost:wght@300;400;500;600;700&display=swap');

    * { box-sizing: border-box; }

    :host {
      --burgundy: #3D1020;
      --burgundy-dk: #2A0916;
      --gold: #F8B414;
      --gold-dk: #D99A0A;
      --dark: #1C2A1C;
      --cream: #FAF0D7;
      --cream-dk: #F0E5C5;
      --muted: #7A5A65;
      --border: #E8D8B4;
    }

    .booking-details-page {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      background: var(--cream);
      font-family: 'Jost', sans-serif;
    }

    .header {
      background: var(--cream);
      border-bottom: 1px solid var(--border);
      padding: 0 20px;
    }

    .header-container {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 70px;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
    }

    .logo-img {
      height: 44px;
      width: auto;
    }

    .nav-links {
      display: flex;
      gap: 30px;
    }

    .nav-links a {
      text-decoration: none;
      color: var(--burgundy);
      font-size: 14px;
      font-weight: 500;
      transition: color 0.2s;
    }

    .nav-links a:hover {
      color: var(--gold-dk);
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .sign-in-link {
      text-decoration: none;
      color: var(--burgundy);
      font-size: 14px;
      font-weight: 500;
    }

    .book-now-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      background: var(--gold);
      color: var(--burgundy);
      text-decoration: none;
      font-size: 14px;
      font-weight: 600;
      border-radius: 4px;
      transition: background 0.2s;
    }

    .book-now-btn:hover {
      background: var(--gold-dk);
    }

    .lang-select {
      padding: 8px 12px;
      border: 1px solid var(--border);
      border-radius: 4px;
      background: var(--cream);
      font-size: 14px;
      cursor: pointer;
      color: var(--burgundy);
    }

    .success-banner {
      background: var(--gold);
      color: var(--burgundy);
      padding: 15px 20px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 600;
      position: relative;
    }

    .error-banner {
      background: var(--burgundy);
      color: var(--cream);
      padding: 15px 20px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 500;
      position: relative;
    }

    .close-btn {
      position: absolute;
      right: 15px;
      background: none;
      border: none;
      color: inherit;
      font-size: 24px;
      cursor: pointer;
      line-height: 1;
      padding: 0;
      opacity: 0.8;
    }

    .close-btn:hover { opacity: 1; }

    .main-content {
      flex: 1;
      padding: 40px 20px;
    }

    .booking-container {
      max-width: 1200px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 400px 1fr;
      gap: 40px;
    }

    .booking-summary {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .summary-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 25px;
      box-shadow: 0 2px 8px rgba(61,16,32,0.07);
    }

    .date-section { margin-bottom: 25px; }

    .date-item { margin-bottom: 15px; }

    .date-label {
      display: block;
      color: var(--muted);
      font-size: 12px;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: .04em;
    }

    .date-value {
      display: block;
      font-size: 16px;
      font-weight: 600;
      color: var(--burgundy);
      font-family: 'Cormorant Garamond', serif;
    }

    .date-time {
      display: block;
      color: var(--muted);
      font-size: 12px;
    }

    .price-breakdown {
      border-top: 1px solid var(--border);
      padding-top: 20px;
    }

    .price-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 14px;
      color: var(--muted);
    }

    .promo-section {
      display: flex;
      gap: 10px;
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid var(--border);
    }

    .promo-input {
      flex: 1;
      padding: 12px 15px;
      border: 1px solid var(--border);
      border-radius: 4px;
      font-size: 14px;
      background: var(--cream);
      color: var(--burgundy);
    }

    .apply-btn {
      padding: 12px 20px;
      background: var(--gold);
      color: var(--burgundy);
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .apply-btn:hover { background: var(--gold-dk); }

    .total-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
      padding-top: 20px;
      border-top: 2px solid var(--gold);
    }

    .total-label {
      font-size: 14px;
      color: var(--muted);
    }

    .total-value {
      font-size: 28px;
      font-weight: 700;
      color: var(--burgundy);
      font-family: 'Cormorant Garamond', serif;
    }

    .info-icon {
      font-size: 14px;
      color: var(--gold-dk);
      cursor: pointer;
    }

    .info-cards {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .info-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 20px;
      display: flex;
      gap: 15px;
      box-shadow: 0 2px 8px rgba(61,16,32,0.07);
    }

    .info-card .info-icon {
      position: relative;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .info-card .badge {
      position: absolute;
      top: -5px;
      right: -5px;
      width: 20px;
      height: 20px;
      background: var(--burgundy);
      color: var(--cream);
      border-radius: 50%;
      font-size: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
    }

    .info-card .badge.green {
      background: var(--gold);
      color: var(--burgundy);
    }

    .info-content h4 {
      margin: 0 0 5px;
      font-size: 14px;
      font-weight: 600;
      color: var(--burgundy);
      font-family: 'Cormorant Garamond', serif;
    }

    .info-content p {
      margin: 0;
      font-size: 13px;
      color: var(--muted);
      line-height: 1.5;
    }

    .details-form {
      background: white;
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 30px;
      box-shadow: 0 2px 8px rgba(61,16,32,0.07);
    }

    .details-form h2 {
      display: flex;
      align-items: center;
      gap: 15px;
      font-size: 28px;
      font-weight: 700;
      color: var(--burgundy);
      margin: 0 0 30px;
      font-family: 'Cormorant Garamond', serif;
    }

    .red-line {
      width: 30px;
      height: 3px;
      background: var(--gold);
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 20px;
    }

    .form-group { margin-bottom: 20px; }

    .form-group.full-width { grid-column: 1 / -1; }

    .form-group label {
      display: block;
      font-size: 13px;
      color: var(--muted);
      margin-bottom: 8px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: .04em;
    }

    .required { color: var(--burgundy); }

    .form-group input,
    .form-group textarea {
      width: 100%;
      padding: 12px 15px;
      border: 1px solid var(--border);
      border-radius: 4px;
      font-size: 14px;
      font-family: 'Jost', sans-serif;
      background: var(--cream);
      color: var(--burgundy);
      transition: border-color 0.2s;
    }

    .form-group input:focus,
    .form-group textarea:focus {
      outline: none;
      border-color: var(--gold);
      box-shadow: 0 0 0 3px rgba(248,180,20,.15);
    }

    .hint {
      display: block;
      font-size: 11px;
      color: var(--muted);
      margin-top: 5px;
    }

    .checkbox-group { margin-bottom: 30px; }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      color: var(--muted);
    }

    .checkbox-label input { width: auto; }

    .room-section { margin-bottom: 30px; }

    .room-section h3 {
      font-size: 20px;
      font-weight: 700;
      color: var(--burgundy);
      margin: 0 0 15px;
      font-family: 'Cormorant Garamond', serif;
    }

    .room-card {
      display: flex;
      gap: 20px;
      padding: 15px;
      background: var(--cream);
      border: 1px solid var(--border);
      border-radius: 8px;
    }

    .room-image {
      width: 120px;
      height: 90px;
      object-fit: cover;
      border-radius: 4px;
      flex-shrink: 0;
    }

    .room-image-placeholder {
      background: linear-gradient(145deg, #2A0916, #3D1020);
    }

    .room-info h4 {
      margin: 0 0 5px;
      font-size: 16px;
      font-weight: 700;
      color: var(--burgundy);
      font-family: 'Cormorant Garamond', serif;
    }

    .room-type {
      margin: 0 0 10px;
      font-size: 13px;
      color: var(--muted);
    }

    .stay-includes {
      margin: 0 0 5px;
      font-size: 13px;
      font-weight: 500;
      color: var(--burgundy);
    }

    .booking-conditions {
      font-size: 13px;
      color: var(--gold-dk);
      text-decoration: none;
      font-weight: 500;
    }

    .booking-conditions:hover { text-decoration: underline; }

    .account-notice {
      display: flex;
      gap: 14px;
      background: rgba(248,180,20,.12);
      border: 1px solid var(--gold);
      border-radius: 6px;
      padding: 16px 18px;
      margin-bottom: 20px;
      align-items: flex-start;
    }

    .account-notice-icon {
      color: var(--gold-dk);
      flex-shrink: 0;
      margin-top: 2px;
    }

    .account-notice-text strong {
      display: block;
      font-size: 14px;
      color: var(--burgundy);
      margin-bottom: 4px;
    }

    .account-notice-text p {
      font-size: 13px;
      color: var(--muted);
      line-height: 1.5;
      margin: 0;
    }

    .submit-btn {
      width: 100%;
      padding: 15px 30px;
      background: var(--burgundy);
      color: var(--cream);
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 600;
      font-family: 'Jost', sans-serif;
      cursor: pointer;
      letter-spacing: .08em;
      text-transform: uppercase;
      transition: background 0.2s;
      margin-top: 20px;
    }

    .submit-btn:hover { background: var(--burgundy-dk); }

    .footer {
      background: var(--dark);
      color: var(--cream);
      padding: 60px 20px 30px;
      margin-top: auto;
    }

    .footer-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    .footer-grid {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 40px;
      margin-bottom: 40px;
    }

    .footer-logo {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
    }

    .footer-logo-img {
      height: 52px;
      width: auto;
    }

    .footer-desc {
      color: rgba(250,240,215,.65);
      font-size: 14px;
      line-height: 1.8;
    }

    .footer-col h4 {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 20px;
      color: var(--gold);
      text-transform: uppercase;
      letter-spacing: .06em;
    }

    .footer-col ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .footer-col ul li { margin-bottom: 12px; }

    .footer-col ul li a {
      color: rgba(250,240,215,.65);
      text-decoration: none;
      font-size: 14px;
      transition: color 0.2s;
    }

    .footer-col ul li a:hover { color: var(--gold); }

    .social-links {
      display: flex;
      gap: 15px;
    }

    .social-links a {
      color: rgba(250,240,215,.65);
      transition: color 0.2s;
    }

    .social-links a:hover { color: var(--gold); }

    .footer-bottom {
      border-top: 1px solid rgba(250,240,215,.15);
      padding-top: 20px;
      text-align: center;
    }

    .footer-bottom p {
      color: rgba(250,240,215,.45);
      font-size: 13px;
      margin: 0;
    }

    @media (max-width: 1024px) {
      .booking-container { grid-template-columns: 1fr; }
      .booking-summary { order: 2; }
      .details-form { order: 1; }
    }

    @media (max-width: 768px) {
      .nav-links { display: none; }
      .form-row { grid-template-columns: 1fr; }
      .footer-grid { grid-template-columns: 1fr 1fr; }
    }
  `]
})
export class BookingDetailsComponent {
  roomCoverImage: any = null;
  protected readonly sanitizer = inject(DomSanitizer);

  booking: BookingDetails = {
    roomName: '',
    roomImage: '',
    roomPrice: 0,
    adults: 0,
    roomSize: 0,
    paymentType: '',
    chambreId: 0,
    checkIn: '',
    checkOut: '',
    taxRate: 10, // Exemple de taux de taxe
    serviceChargeRate: 5, // Exemple de taux de service,
    numberOfRooms: 0,
  };
  guestDetails = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    fullName: '',
    specialRequests: '',
    dateNaissance: ''
  };

  occupantDetails = {
    nombreAdultes: 2,
    nombreEnfants: 0,
    avecAnimalCompagnie: false,
    ageEnfantSuperieurASept: false,
    totalOccupants: 2
  };

  promoCode = '';
  isCreatedCompte = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private bookingService: BookingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialiser la langue par défaut au français
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');

    if (isPlatformBrowser(this.platformId)) {
      const state = history.state;
      if (state && state.room) {
        this.booking = {
          ...this.booking,
          roomName: state.room.name || this.booking.roomName,
          roomPrice: state.room.price || this.booking.roomPrice,
          adults: state.adults || state.room.adults || this.booking.adults,
          roomSize: state.room.size || this.booking.roomSize,
          paymentType: state.room.paymentType || this.booking.paymentType,
          chambreId: state.room.id || this.booking.chambreId
        };
        this.roomCoverImage = state.room.coverImage || null;
        this.occupantDetails.nombreAdultes = this.booking.adults;
        this.occupantDetails.totalOccupants = this.booking.adults;
      }
      if (state && state.checkIn) {
        this.booking.checkIn = state.checkIn;
      }
      if (state && state.checkOut) {
        this.booking.checkOut = state.checkOut;
      }
      if (state && state.children) {
        this.occupantDetails.nombreEnfants = state.children;
        this.occupantDetails.totalOccupants = this.occupantDetails.nombreAdultes + state.children;
      }
    }
    // Le compte doit toujours être créé pour permettre le paiement en ligne
    this.isCreatedCompte = true;
  }

  public authService = inject(AuthService);

  getImageSrc(images: DocumentDto | undefined, sanitizer: DomSanitizer): SafeUrl | string {
    const img = images;
    if (!img) return ' ';
    if (img.documentUrl) return sanitizer.bypassSecurityTrustUrl(img.documentUrl);
    if (img.content) {
      const mime = img.contentType || 'image/jpeg';
      return sanitizer.bypassSecurityTrustUrl(`data:${mime};base64,${img.content}`);
    }
    return '';
  }

  changeLanguage(event: Event) {
    const lang = (event.target as HTMLSelectElement).value;
    this.translate.use(lang);
  }

  get taxAmount(): number {
    return this.booking.roomPrice * (this.booking.taxRate / 100);
  }

  get serviceChargeAmount(): number {
    return this.booking.roomPrice * (this.booking.serviceChargeRate / 100);
  }

  get totalPrice(): number {
    return this.booking.roomPrice + this.taxAmount + this.serviceChargeAmount;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    const lang = (this.translate && (this.translate.currentLang || this.translate.getDefaultLang())) || 'en';
    const locale = lang && lang.toLowerCase().startsWith('fr') ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, options);
  }

  applyPromoCode() {
    console.log('Applying promo code:', this.promoCode);
  }

  onSubmit() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;

    // Mise à jour du total des occupants
    this.occupantDetails.totalOccupants = this.occupantDetails.nombreAdultes + this.occupantDetails.nombreEnfants;

    const reservationRequest: ReservationRequest = {
      clientId: 0, // 0 pour créer un nouveau client
      client: {
        nom: this.guestDetails.lastName,
        prenom: this.guestDetails.firstName,
        email: this.guestDetails.email,
        telephone: this.guestDetails.phone,
        adresse: this.guestDetails.address,
        dateNaissance: this.guestDetails.dateNaissance || undefined
      },
      chambreId: this.booking.chambreId,
      dateArrivee: this.booking.checkIn,
      dateDepart: this.booking.checkOut,
      nombrePersonnes: this.occupantDetails.totalOccupants,
      occupantDetails: {
        nombreAdultes: this.occupantDetails.nombreAdultes,
        nombreEnfants: this.occupantDetails.nombreEnfants,
        avecAnimalCompagnie: this.occupantDetails.avecAnimalCompagnie,
        ageEnfantSuperieurASept: this.occupantDetails.ageEnfantSuperieurASept,
        totalOccupants: this.occupantDetails.totalOccupants
      },
      isCreatedCompte: this.isCreatedCompte
    };

    this.bookingService.createReservation(reservationRequest).subscribe({
      next: (response) => {
        console.log('Reservation created successfully:', response);
        this.isSubmitting = false;
        this.errorMessage = '';
        this.successMessage =
          `Réservation enregistrée ! Un compte a été créé avec l'email ${this.guestDetails.email}. ` +
          `Connectez-vous pour confirmer votre réservation par paiement en ligne.`;
        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        }, 4000);
      },
      error: (error) => {
        console.error('Error creating reservation:', error);
        this.isSubmitting = false;
        this.successMessage = '';
        // Afficher le message d'erreur de l'API
        if (error.error && error.error.message) {
          this.errorMessage = error.error.message;
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = 'Erreur lors de la réservation. Veuillez réessayer.';
        }
        // Scroll vers le haut pour afficher l'erreur
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }
}
